import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import LgmButton from '../LgmButton';
import { useModalBackDismiss } from 'navigation/useModalBackDismiss';
import { useApi } from '../../services/api/provider';
import { ProductService } from '../../services/api/domains/products/service';
import {
  ExternalLinkSource,
  ExternalLinkConsequences,
  BreakLinkResponse,
  BreakLinkConflictBody,
} from '../../services/api/domains/products/types';
import { ApiError } from '../../services/api/types';
import { HTTP_CONFLICT } from '../../constants/httpStatus';
import { GRAY_500, GRAY_900 } from 'theme/grayScale';
import { COLOR_DANGER, COLOR_DANGER_TEXT } from 'theme/colors';

// ---------------------------------------------------------------------------
// Consequence rendering — shared between the "ready" and "conflict" phases
// ---------------------------------------------------------------------------

const ConsequenceRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div style={{ display: 'flex', gap: '8px' }}>
    <span
      style={{
        minWidth: '160px',
        fontWeight: 600,
        fontSize: '0.8125rem',
        color: GRAY_500,
        flexShrink: 0,
      }}>
      {label}
    </span>
    <span style={{ fontSize: '0.8125rem', color: GRAY_900 }}>{value}</span>
  </div>
);

const ConsequencesSummary: React.FC<{
  consequences: ExternalLinkConsequences;
  sourceLabel: string;
}> = ({ consequences, sourceLabel }) => {
  if (!consequences.link_exists) {
    return (
      <Typography variant="body2" color="text.secondary">
        This link is no longer active on this product — there is nothing to
        break. It may have already been removed by another admin.
      </Typography>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <ConsequenceRow
        label="Name variant removed"
        value={
          consequences.name_variant_removed?.name ??
          '— no name was synced from this record'
        }
      />
      <ConsequenceRow
        label={`${sourceLabel} link flag`}
        value={
          consequences.source_flag?.will_flip_to_false
            ? `Will clear — this is the last active ${sourceLabel} link`
            : `Stays set — other ${sourceLabel} links remain`
        }
      />
      {consequences.preferred_name_match.matches && (
        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: '6px',
            padding: '8px 12px',
          }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: '#92400e' }}>
            This name is the current LGM preferred name
          </Typography>
          <Typography variant="body2" sx={{ color: '#92400e', mt: 0.25 }}>
            Breaking this link removes &quot;
            {consequences.preferred_name_match.preferred_product_name}&quot; — a
            new preferred name may need to be selected afterward.
          </Typography>
        </div>
      )}
      {consequences.dependent_links_dropped.length > 0 && (
        <div>
          <Typography variant="body2" fontWeight={600}>
            Also drops {consequences.dependent_links_dropped.length} dependent
            link
            {consequences.dependent_links_dropped.length === 1 ? '' : 's'}:
          </Typography>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
            {consequences.dependent_links_dropped.map(d => (
              <li key={`${d.source}-${d.record_id}`}>
                <Typography variant="body2" color="text.secondary">
                  {d.source.toUpperCase()} —{' '}
                  {d.display_name ?? `record ${d.record_id}`} (via{' '}
                  {d.match_basis})
                </Typography>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// BreakLinkConfirmDialog — issue #622, Sub-task 8.3
// ---------------------------------------------------------------------------

type DialogPhase =
  | 'loading-preview'
  | 'preview-ready'
  | 'preview-unavailable'
  | 'breaking'
  | 'break-error'
  | 'conflict';

interface ConflictData {
  previous: ExternalLinkConsequences;
  current: ExternalLinkConsequences;
}

export interface BreakLinkConfirmDialogProps {
  productId: number;
  source: ExternalLinkSource;
  /** Human-readable source name, e.g. "Orange Book", "NDC", "USDMF". */
  sourceLabel: string;
  recordId: number;
  /** Short identifying label for the specific record, e.g. its trade name or subject. */
  recordLabel: string;
  onClose: () => void;
  onBroken: (result: BreakLinkResponse) => void;
}

/**
 * Confirmation dialog for breaking one external source link (issue #622,
 * Sub-task 8.3). Shared across OrangeBookPanel, DMFPanel, and NDCPanel
 * rather than three copies — the preview-fetch / consequence-display /
 * commit / 409-conflict state machine is identical for every source, only
 * the source key and display labels differ per caller.
 *
 * On open, calls the Task 6 preview endpoint
 * (`GET /api/external-links/:productId/:source/:recordId/preview`) and
 * renders the REAL computed consequences. If that call fails, the dialog
 * says consequences are explicitly UNKNOWN (Sub-task 8.3 AC3) rather than
 * degrading to a generic confirmation — the admin is confirming blind, and
 * the copy says so.
 *
 * Confirming calls the Task 6 break-link endpoint
 * (`DELETE /api/external-links/:productId/:source/:recordId`), echoing the
 * preview's `comparable` object back as `expected_consequences` so the
 * server's commit-time recompute can validate nothing changed (Architecture
 * Decision 19). A 409 response means the consequences changed since they
 * were shown — the dialog surfaces the updated impact explicitly and
 * requires a fresh confirm, rather than silently retrying or showing a
 * generic error.
 */
const BreakLinkConfirmDialog: React.FC<BreakLinkConfirmDialogProps> = ({
  productId,
  source,
  sourceLabel,
  recordId,
  recordLabel,
  onClose,
  onBroken,
}) => {
  const api = useApi();
  const productService = React.useMemo(() => new ProductService(api), [api]);

  const [phase, setPhase] = React.useState<DialogPhase>('loading-preview');
  const [consequences, setConsequences] =
    React.useState<ExternalLinkConsequences | null>(null);
  const [conflictData, setConflictData] = React.useState<ConflictData | null>(
    null
  );
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const loadPreview = React.useCallback(async () => {
    setPhase('loading-preview');
    setErrorMessage(null);
    try {
      const response = await productService.previewBreakExternalLink(
        productId,
        source,
        recordId
      );
      setConsequences(response.data.comparable);
      setPhase('preview-ready');
    } catch {
      // Preview unavailable — consequences are explicitly unknown, not
      // silently treated as "no consequences" (Sub-task 8.3 AC3).
      setConsequences(null);
      setPhase('preview-unavailable');
    }
  }, [productService, productId, source, recordId]);

  React.useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const isBreaking = phase === 'breaking';

  useModalBackDismiss({
    isOpen: true,
    onClose,
    canDismiss: () => !isBreaking,
  });

  const handleConfirm = async () => {
    setPhase('breaking');
    setErrorMessage(null);
    try {
      const response = await productService.breakExternalLink(
        productId,
        source,
        recordId,
        consequences
      );
      onBroken(response.data);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.response?.status === HTTP_CONFLICT) {
        const body = apiErr.response.data as BreakLinkConflictBody;
        setConflictData({
          previous: body.previous_consequences,
          current: body.current_consequences,
        });
        setPhase('conflict');
      } else {
        setErrorMessage(
          apiErr instanceof Error
            ? apiErr.message
            : 'Failed to break the link. Try again.'
        );
        setPhase('break-error');
      }
    }
  };

  // The admin must explicitly review the new impact before the Confirm
  // button reappears — this is a re-confirm step, not an auto-retry.
  const handleReviewUpdatedImpact = () => {
    if (conflictData) {
      setConsequences(conflictData.current);
    }
    setConflictData(null);
    setPhase('preview-ready');
  };

  const confirmDisabled =
    isBreaking ||
    phase === 'loading-preview' ||
    (phase === 'preview-ready' &&
      consequences !== null &&
      !consequences.link_exists);

  return (
    <Dialog
      open
      onClose={isBreaking ? undefined : onClose}
      sx={{ zIndex: 20000 }}
      slotProps={{ paper: { sx: { minWidth: 440, maxWidth: 560 } } }}
      aria-labelledby="break-link-dialog-title">
      <DialogTitle
        id="break-link-dialog-title"
        sx={{ fontWeight: 600, fontSize: '1rem' }}>
        Break link to {sourceLabel}?
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {sourceLabel} record: {recordLabel}
        </Typography>

        {phase === 'loading-preview' && (
          <Typography variant="body2" color="text.secondary">
            Loading consequences…
          </Typography>
        )}

        {phase === 'preview-unavailable' && (
          <div
            role="alert"
            style={{
              background: '#fef2f2',
              border: `1px solid ${COLOR_DANGER}`,
              borderRadius: '6px',
              padding: '12px 16px',
            }}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: COLOR_DANGER_TEXT }}>
              Consequences are UNKNOWN
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: COLOR_DANGER_TEXT, mt: 0.5 }}>
              The consequence preview could not be loaded. Breaking this link
              may remove a name variant, clear the {sourceLabel} link flag, or
              drop dependent links in other sources — none of that can be
              confirmed right now.
            </Typography>
          </div>
        )}

        {(phase === 'preview-ready' ||
          phase === 'breaking' ||
          phase === 'break-error') &&
          consequences && (
            <ConsequencesSummary
              consequences={consequences}
              sourceLabel={sourceLabel}
            />
          )}

        {phase === 'conflict' && conflictData && (
          <div
            role="alert"
            style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '6px',
              padding: '12px 16px',
            }}>
            <Typography variant="body2" fontWeight={600}>
              The consequences changed since they were last shown
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Another admin or an automatic linker changed something between the
              preview and this confirmation. Review the updated impact below
              before confirming again.
            </Typography>
            <div style={{ marginTop: '8px' }}>
              <ConsequencesSummary
                consequences={conflictData.current}
                sourceLabel={sourceLabel}
              />
            </div>
          </div>
        )}

        {phase === 'break-error' && errorMessage && (
          <Typography variant="body2" sx={{ color: COLOR_DANGER_TEXT }}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <LgmButton variant="outlined" onClick={onClose} disabled={isBreaking}>
          Cancel
        </LgmButton>
        {phase === 'conflict' ? (
          <LgmButton color="danger" onClick={handleReviewUpdatedImpact}>
            Review updated impact
          </LgmButton>
        ) : (
          <LgmButton
            color="danger"
            onClick={handleConfirm}
            disabled={confirmDisabled}>
            {isBreaking ? 'Breaking…' : 'Break link'}
          </LgmButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BreakLinkConfirmDialog;
